import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  SESSION_EVENTS,
  SESSION_NAMESPACE,
  type ProgressChangedPayload,
  type RumRevealedPayload,
  type SessionClosedPayload,
  type TastingOpenedPayload,
} from '@cercle/shared';

export interface SessionSocketHandlers {
  onTastingOpened?: (payload: TastingOpenedPayload) => void;
  onRumRevealed?: (payload: RumRevealedPayload) => void;
  onSessionClosed?: (payload: SessionClosedPayload) => void;
  onProgressChanged?: (payload: ProgressChangedPayload) => void;
}

/**
 * Abonnement au namespace `/session` de la séance courante (US-15).
 *
 * Reconnexion automatique avec backoff : en salle Courbet la connexion tombe et revient,
 * et manquer la révélation parce que le socket n'est pas revenu serait exactement le
 * problème qu'on cherche à éviter. L'authentification passe par le cookie httpOnly, déjà
 * envoyé par le handshake — il n'y a pas de jeton à manipuler en JavaScript.
 */
export function useSessionSocket(
  sessionId: string | null,
  handlers: SessionSocketHandlers,
): void {
  // Les gestionnaires changent à chaque rendu ; les garder dans une ref évite de
  // reconstruire le socket à chaque fois.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!sessionId) return;

    const socket: Socket = io(`${socketOrigin()}${SESSION_NAMESPACE}`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });

    const join = () => socket.emit('session:join', sessionId);
    socket.on('connect', join);

    socket.on(SESSION_EVENTS.TASTING_OPENED, (p: TastingOpenedPayload) =>
      handlersRef.current.onTastingOpened?.(p),
    );
    socket.on(SESSION_EVENTS.RUM_REVEALED, (p: RumRevealedPayload) =>
      handlersRef.current.onRumRevealed?.(p),
    );
    socket.on(SESSION_EVENTS.SESSION_CLOSED, (p: SessionClosedPayload) =>
      handlersRef.current.onSessionClosed?.(p),
    );
    socket.on(SESSION_EVENTS.PROGRESS_CHANGED, (p: ProgressChangedPayload) =>
      handlersRef.current.onProgressChanged?.(p),
    );

    return () => {
      socket.emit('session:leave', sessionId);
      socket.disconnect();
    };
  }, [sessionId]);
}

function socketOrigin(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  return configured ?? window.location.origin;
}
